/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, notification } from 'antd';
import { connect } from 'react-redux';
import { signIn } from '../../redux/actions/authActions';
import get from 'lodash/get';
import { bool, func } from 'prop-types';
import logoCrc from '../../assets/logoCrc.png';
import history from '../../services/history';
import { EyeInvisibleOutlined, EyeOutlined, CheckCircleFilled } from '@ant-design/icons';
import { Button as CrcButton } from '@crc-evans/ui-components';
import * as constants from '../../utils/constants';
import {
  userSignIn,
  userSignOut,
  getMfaStatus,
  sendLoginOtp,
  verifyLoginOtp,
  isMockAuthEnabled,
} from '../../services/auth';

const Login = ({ onSignIn, showLoader, authChallenge }) => {
  const [loginForm] = Form.useForm();

  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const [localError, setLocalError] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [cognitoUserId, setCognitoUserId] = useState(null);

  const otpRefs = useRef([]);

  useEffect(() => {
    if (timer > 0) {
      const intervalId = setInterval(() => {
        setTimer((prevTime) => prevTime - 1);
      }, 1000);
      return () => clearInterval(intervalId);
    } else if (timer === 0 && !canResend) {
      setCanResend(true);
    }
  }, [timer, canResend]);

  useEffect(() => {
    if (authChallenge !== '') {
      if (authChallenge === constants.CHALLENGE_NAME) {
        const formValues = loginForm.getFieldsValue();
        history.push('/reset-password', {
          email: formValues.username || credentials?.username || '',
          isRedirectedFromLogin: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChallenge]);

  const handleSendOtp = async (email) => {
    if (!email) {
      setLocalError('Please enter your email address');
      return;
    }
    try {
      setIsSendingOtp(true);
      setLocalError(null);
      const response = await sendLoginOtp({ email });
      console.log('OTP send response:', response);
      setOtpSent(true);
      setTimer(30);
      setCanResend(false);
      notification.success({ message: 'OTP sent to your email' });
      setTimeout(() => { otpRefs.current[0]?.focus(); }, 100);
    } catch (err) {
      console.error('Error sending OTP:', err);
      const errorMsg = err.message || err.response?.data?.message || 'Failed to send OTP. Please try again.';
      setLocalError(errorMsg);
      notification.error({ message: errorMsg });
      setOtpSent(false);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend || !credentials) return;
    await handleSendOtp(credentials.username);
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) { setLocalError('Please enter a valid 6-digit OTP'); return; }
    if (!credentials) { setLocalError('Credentials not found. Please start over.'); setOtpSent(false); return; }
    try {
      setIsVerifying(true);
      setLocalError(null);
      const res = await verifyLoginOtp({
        email: credentials.username,
        code: otp,
        ...(credentials.userId && { user_id: credentials.userId }),
      });
      const isValid = res?.data?.success || res?.data?.valid || res?.success === 1;
      if (isValid) {
        setOtpVerified(true);
        notification.success({ message: 'OTP verified successfully' });
        sessionStorage.removeItem('otp_validation_in_progress');
        await onSignIn(credentials);
      } else {
        const errorMsg = 'Invalid OTP. Please try again.';
        setLocalError(errorMsg);
        notification.error({ message: errorMsg });
        setOtp('');
        setTimeout(() => { otpRefs.current[0]?.focus(); }, 0);
      }
    } catch (err) {
      const errorMsg = err?.message || err?.response?.data?.message || 'Verification failed. Please retry.';
      setLocalError(errorMsg);
      notification.error({ message: errorMsg });
      setOtp('');
      setTimeout(() => { otpRefs.current[0]?.focus(); }, 0);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async (values) => {
    const email = values.username.toLowerCase().trim();
    const password = values.password;
    if (!email || !password) { setLocalError('Please enter both email and password'); return; }

    if (isMockAuthEnabled()) {
      setCredentials({ username: email, password });
      try {
        setIsValidating(true);
        setLocalError(null);
        sessionStorage.removeItem('otp_validation_in_progress');
        await onSignIn({ username: email, password });
      } finally {
        setIsValidating(false);
      }
      return;
    }

    if (otpSent) return;

    setCredentials({ username: email, password });
    try {
      setIsValidating(true);
      setLocalError(null);
      sessionStorage.setItem('otp_validation_in_progress', 'true');
      const result = await userSignIn(email, password);
      if (result && result.challengeName) { setIsValidating(false); return; }
      const userId = result?.signInUserSession?.getIdToken?.()?.payload?.sub ?? null;
      setCredentials((prev) => prev ? { ...prev, userId } : { username: email, password, userId });
      localStorage.removeItem('ap_idToken');
      localStorage.removeItem('ap_rToken');
      localStorage.removeItem('user');
      try { await userSignOut(); } catch (signOutErr) { console.log('Sign out during validation:', signOutErr); }
      await new Promise((resolve) => setTimeout(resolve, 200));
      try {
        const mfaRes = await getMfaStatus(email);
        if (mfaRes?.data?.requireOtp === false) {
          sessionStorage.removeItem('otp_validation_in_progress');
          setIsValidating(false);
          await onSignIn({ username: email, password, userId });
          return;
        }
      } catch (mfaErr) {
        const mfaMsg = mfaErr?.message || mfaErr?.response?.data?.message || 'Unable to check login options. Please try again.';
        setLocalError(mfaMsg);
        notification.error({ message: mfaMsg });
        sessionStorage.removeItem('otp_validation_in_progress');
        setIsValidating(false);
        return;
      }
      setIsValidating(false);
      await handleSendOtp(email);
    } catch (err) {
      let errorMessage = 'Invalid username or password';
      if (err.message) {
        if (err.message.includes('Incorrect username or password')) errorMessage = 'Incorrect username or password';
        else if (err.message.includes('User does not exist')) errorMessage = 'User does not exist';
        else if (err.message.includes('User is not confirmed')) errorMessage = 'Please verify your email address first';
        else errorMessage = err.message;
      }
      setLocalError(errorMessage);
      notification.error({ message: errorMessage });
      sessionStorage.removeItem('otp_validation_in_progress');
    } finally {
      setIsValidating(false);
    }
  };

  const handleOtpChange = (index, value) => {
    const sanitizedValue = value.replace(/\D/, '');
    const newOtp = otp.split('');
    newOtp[index] = sanitizedValue;
    const updatedOtp = newOtp.join('');
    setOtp(updatedOtp);
    setLocalError(null);
    if (sanitizedValue && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '').slice(0, 6);
    setOtp(digits);
    setLocalError(null);
    const focusIndex = Math.min(digits.length, 5);
    setTimeout(() => { otpRefs.current[focusIndex]?.focus(); }, 0);
  };

  const handleResetOtp = () => {
    setOtpSent(false);
    setOtp('');
    setOtpVerified(false);
    setTimer(0);
    setCanResend(true);
    setLocalError(null);
    setCredentials(null);
    setCognitoUserId(null);
    sessionStorage.removeItem('otp_validation_in_progress');
  };

  const isLoading = showLoader || isSendingOtp || isValidating;

  return (
    <div className="crce-login-root">
      <div className="crce-login-right">
        <div className="crce-login-form-card">

          {/* Header */}
          <div className="crce-login-header">
            <h1 className="crce-login-title">CRCE Connect</h1>
            <p className="crce-login-subtitle">
              {otpSent ? 'Two-Factor Verification' : 'Sign in to your account'}
            </p>
          </div>

          {/* Error banner */}
          {localError && (
            <div className="crce-login-error">
              <span className="crce-login-error-icon">⚠</span>
              {localError}
            </div>
          )}

          <Form
            form={loginForm}
            name="crce_login"
            initialValues={{ remember: true }}
            onFinish={handleSubmit}
            layout="vertical"
          >
            {/* ── Step 1: Email + Password ── */}
            {!otpSent && (
              <>
                <Form.Item
                  label={<span className="crce-login-label">Email Address</span>}
                  name="username"
                  rules={[
                    { required: true, message: 'Please input your email address!' },
                    { pattern: constants.EMAIL_PATTERN, message: 'Provide a valid email.' },
                  ]}
                >
                  <Input
                    className="crce-login-input"
                    placeholder="you@company.com"
                    disabled={isLoading}
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  label={<span className="crce-login-label">Password</span>}
                  name="password"
                  rules={[{ required: true, message: 'Please input your Password!' }]}
                >
                  <Input.Password
                    className="crce-login-input"
                    placeholder="Enter your password"
                    disabled={isLoading}
                    size="large"
                    iconRender={(visible) =>
                      visible ? (
                        <EyeOutlined className="crce-login-eye-icon" />
                      ) : (
                        <EyeInvisibleOutlined className="crce-login-eye-icon" />
                      )
                    }
                  />
                </Form.Item>

                <Form.Item className="crce-login-submit-item" style={{ marginTop: '8px', marginBottom: '12px' }}>
                  <button
                    type="submit"
                    className="crce-login-native-submit"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Validating...' : 'Sign In'}
                  </button>
                </Form.Item>
              </>
            )}

            {/* ── Step 2: OTP Verification ── */}
            {otpSent && !otpVerified && credentials && (
              <div className="crce-otp-section">
                <div className="crce-otp-badge">
                  <CheckCircleFilled className="crce-otp-badge-icon" />
                  <span>OTP sent to <strong>{credentials?.username}</strong></span>
                </div>

                <p className="crce-otp-hint">Enter the 6-digit code sent to your email</p>

                <div className="crce-otp-input-group">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <input
                      key={index}
                      ref={(el) => (otpRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={otp[index] || ''}
                      className={`crce-otp-box${otp[index] ? ' filled' : ''}`}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={index === 0 ? handleOtpPaste : undefined}
                      disabled={isVerifying}
                    />
                  ))}
                </div>

                <div className="crce-otp-resend">
                  <span className="crce-otp-resend-text">Didn&apos;t receive code?</span>
                  <button
                    type="button"
                    className={`crce-otp-resend-btn${!canResend || isVerifying ? ' disabled' : ''}`}
                    disabled={!canResend || isVerifying}
                    onClick={handleResendOtp}
                  >
                    {canResend ? 'Resend OTP' : `Resend in ${timer}s`}
                  </button>
                </div>

                <Form.Item style={{ marginBottom: '10px' }}>
                  <CrcButton
                    label={isVerifying ? 'Verifying...' : showLoader ? 'Signing In...' : 'Verify & Sign In'}
                    variant="primary"
                    size="large"
                    disabled={otp.length !== 6 || isVerifying || showLoader}
                    onClick={handleVerifyOtp}
                    style={{ width: '100%' }}
                  />
                </Form.Item>

                <button
                  type="button"
                  className="crce-otp-back-btn"
                  onClick={handleResetOtp}
                  disabled={isVerifying || showLoader}
                >
                  ← Back to Login
                </button>
              </div>
            )}

            {/* OTP Verified state */}
            {otpVerified && (
              <div className="crce-otp-verified">
                <CheckCircleFilled className="crce-otp-verified-icon" />
                <span>OTP verified. Signing you in…</span>
              </div>
            )}
          </Form>

          {/* Footer */}
          <p className="crce-login-footer">
            Pioneered by CRC Evans &copy;{new Date().getFullYear()}
            {' · '}
            <a href="https://www.crcevans.com/privacy-policy/" rel="noreferrer" target="_blank">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

Login.propTypes = {
  onSignIn: func.isRequired,
  showLoader: bool.isRequired,
};

const mapStateToProps = ({ auth }) => {
  const showLoader = get(auth, 'isLoginLoading', false);
  const authChallenge = get(auth, 'challengeName', '');
  return { showLoader, authChallenge };
};

const mapDispatchToProps = { onSignIn: signIn };

export default connect(mapStateToProps, mapDispatchToProps)(Login);
